import { useEffect, useState, useRef } from 'react'
import { MODEL_TYPES } from './machineLearning/params';
import HandsHandler from './machineLearning/handlers/handsHandler';
import PosesHandler from './machineLearning/handlers/posesHandler';
import ImagesHandler from './machineLearning/handlers/imagesHandler';
import ObjectsHandler from './machineLearning/handlers/objectsHandler';
import SoundsHandler from './machineLearning/handlers/soundsHandler';
import TextsHandler from './machineLearning/handlers/textsHandler';
import FacesHandler from './machineLearning/handlers/facesHandler';


export default function useMachineLearning({ trainingParams = {}, wrapperClass = 'canvas-wrapper', getPredictionCallback = null }) {
  const handler = useRef(null)

  const getModelHandler = (modelType) => {
    switch (modelType) {
      case MODEL_TYPES.HANDS:
        return new HandsHandler(wrapperClass, getPredictionCallback);
      case MODEL_TYPES.POSES:
        return new PosesHandler(wrapperClass, getPredictionCallback);
      case MODEL_TYPES.IMAGES:
        return new ImagesHandler(wrapperClass, getPredictionCallback);
      case MODEL_TYPES.OBJECTS:
        return new ObjectsHandler(wrapperClass, getPredictionCallback);
      case MODEL_TYPES.SOUNDS:
        return new SoundsHandler(wrapperClass, getPredictionCallback);
      case MODEL_TYPES.TEXTS:
        return new TextsHandler(wrapperClass, getPredictionCallback);
      case MODEL_TYPES.FACES:
        return new FacesHandler(wrapperClass, getPredictionCallback);
      default:
        throw new Error(`Unknown model type: ${modelType}`);
    }
  }

  const init = async (modelType) => {
    handler.current = getModelHandler(modelType);
    await handler.current.init();
    return handler.current;
  }

  const getDefaultTrainingParams = () => {
    return handler.current.getTrainingParams();
  }

  const getMinSamplesRequired = () => {
    return handler.current.getMinSamplesRequired();
  }

  const startMedia = async (CameraWrapper) => {
    if (handler.current) {
      await handler.current.startMedia(CameraWrapper);
    }
  }

  const stopMedia = () => {
    if (handler.current) {
      handler.current.stopMedia();
    }
  }

  const addClass = (className = '') => {
    return handler.current.addClass(className);
  }

  const deleteClass = (id) => {
    return handler.current?.deleteClass(id);
  }

  const editClassName = (id, name) => {
    return handler.current?.editClassName(id, name);
  }


  const addSample = async (className, text = null, audioBuffer = null) => {
    return (await handler.current.addSample(className, text, audioBuffer)) || 0;
  }

  const getSamples = (getTensors = undefined) => {
    return handler.current ? handler.current.getSamples(getTensors) : null;
  }

  const importSamples = async (samples) => {
    return handler.current.importSamples(samples);
  }

  const deleteSampleImages = (id, indexes) => {
    handler.current?.deleteSampleImages(id, indexes)
  }

  const clearAllSamples = () => {
    handler.current.clearAllSamples();
  }

  const train = async (callbacks) => {
    return handler.current.train(callbacks);
  }

  const startClassifying = () => {
    if (handler.current) {
      handler.current.startClassifying();
    }
  }

  const stopClassifying = () => {
    if (handler.current) {
      handler.current.stopClassifying();
    }
  }

  const saveModelToDB = async () => {
    return handler.current.saveModelToDB();
  }

  const loadModelFromDB = async (json, weights) => {
    try {
      const userClassNames = await handler.current.loadModelFromDB(json, weights);
      return userClassNames;
    } catch (error) {
      console.log('=========================Error loading model:', error);
      return null;
    }
  }

  const loadModel = async (json, weights) => {
    try {
      const userClassNames = await handler.current.loadModel(json, weights);
      return userClassNames;
    } catch (error) {
      console.log('=========================Error loading model:', error);
      return null;
    }
  }

  useEffect(() => {
    if (handler.current) {
      handler.current.setTrainingParams(trainingParams);
    }
  }, [trainingParams]);

  return {
    getDefaultTrainingParams,
    getMinSamplesRequired,
    init,
    startMedia,
    stopMedia,
    addClass,
    deleteClass,
    deleteSampleImages,
    editClassName,
    addSample,
    getSamples,
    importSamples,
    clearAllSamples,
    loadModel,
    saveModelToDB,
    loadModelFromDB,
    train,
    startClassifying,
    stopClassifying,
  };
}
